import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "../components/ui/Button";
import { cn } from "../components/ui/cn";
import { toPreview } from "../components/editor/CannedPicker";
import { FormatToolbar } from "../components/editor/FormatToolbar";
import { RichText, type RichTextHandle } from "../components/editor/RichText";
import type { CannedKind, CannedResponse } from "../types";

/**
 * The shared snippet library.
 *
 * Editable by any agent, deliberately: the people who know the right wording
 * are the ones answering the mail, and routing every phrasing tweak through an
 * admin is how a canned library goes stale and stops being used.
 *
 * ## Two libraries on one page
 *
 * Reply snippets and subject templates are the same idea applied to the two
 * halves of an email, and they are managed together because they are written
 * together -- somebody adding a "Returns" snippet wants "Your return, ref
 * [[RMA]]" on the subject line of the same mail. Separate pages would put two
 * halves of one thought behind two links.
 *
 * They are still shown as two sections rather than mixed, because the thing
 * you are looking for is always one or the other: nobody scans this page
 * wondering whether the entry they want is a subject or a body.
 */
export function CannedResponses() {
  const queryClient = useQueryClient();
  /*
   * The kind is fixed when the editor opens rather than chosen inside it.
   *
   * "New" is two buttons, so the decision is made where the two libraries are
   * visible and the form arrives already being the right shape. A kind
   * dropdown inside the form would have to swap the content field under
   * somebody mid-sentence, and there is no good behaviour for that.
   */
  const [editing, setEditing] = useState<
    CannedResponse | { kind: CannedKind } | null
  >(null);

  const responses = useQuery({
    queryKey: queryKeys.canned,
    queryFn: api.cannedResponses,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.canned });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteCannedResponse(id),
    onSuccess: invalidate,
  });

  const all = responses.data ?? [];
  const bodies = all.filter((entry) => entry.kind !== "subject");
  const subjects = all.filter((entry) => entry.kind === "subject");

  return (
    <div className="mx-auto h-full w-full max-w-4xl overflow-y-auto p-4 sm:p-6">
      {/* Wraps: the buttons cannot shrink below their own labels, so on a
          phone they take the next line rather than pushing the page sideways.
          The heading is a full line there (`basis-full`) because, sharing one
          with two buttons, a zero-basis heading was squeezed to a column one
          word wide. From `sm` it shares the row as before. */}
      <div className="mb-6 flex flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          <h1 className="mb-1 text-lg font-semibold text-content">
            Canned responses
          </h1>
          <p className="text-sm text-muted">
            Reusable wording for both halves of an email. Type{" "}
            <code className="rounded bg-surface-2 px-1 text-xs">#shortcut</code>{" "}
            in the reply box for a snippet, or in a subject line for a subject
            template.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setEditing({ kind: "subject" })}>
          New subject template
        </Button>
        <Button onClick={() => setEditing({ kind: "body" })}>New response</Button>
      </div>

      {editing ? (
        <Editor
          key={"id" in editing ? editing.id : `new-${editing.kind}`}
          response={"id" in editing ? editing : null}
          kind={editing.kind}
          existing={all}
          onDone={() => {
            setEditing(null);
            invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {all.length === 0 && !editing ? (
        <p className="rounded-lg border border-dashed border-line p-8 text-center text-sm text-muted">
          No canned responses yet.
        </p>
      ) : null}

      <Library
        title="Reply snippets"
        entries={bodies}
        onEdit={setEditing}
        onDelete={(id) => remove.mutate(id)}
      />
      <Library
        title="Subject templates"
        entries={subjects}
        onEdit={setEditing}
        onDelete={(id) => remove.mutate(id)}
        /* Only when the desk has none. A section header over nothing is worth
           it once, to say the feature exists; a second empty header under a
           populated one is just noise. */
        emptyHint="Nothing yet. A subject template fills the subject line the same way a snippet fills the body."
      />
    </div>
  );
}

/**
 * One of the two libraries, grouped by category.
 *
 * Extracted when subject templates arrived: the two render identically, and a
 * copy of this markup was how the reply list and the subject list would start
 * to disagree about, say, whether an inactive entry is greyed.
 */
function Library({
  title,
  entries,
  onEdit,
  onDelete,
  emptyHint,
}: {
  title: string;
  entries: CannedResponse[];
  onEdit: (entry: CannedResponse) => void;
  onDelete: (id: number) => void;
  emptyHint?: string;
}) {
  if (entries.length === 0) {
    if (!emptyHint) return null;
    return (
      <section className="mb-6">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">
          {title}
        </h2>
        <p className="rounded-lg border border-dashed border-line p-6 text-center text-xs text-muted">
          {emptyHint}
        </p>
      </section>
    );
  }

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">
        {title}
      </h2>

      {groupByCategory(entries).map(([category, rows]) => (
        <section key={category} className="mb-4">
          <h3 className="mb-2 text-[11px] font-medium text-muted">{category}</h3>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {rows.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-surface-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={cn(
                        "truncate text-sm font-medium",
                        entry.isActive ? "text-content" : "text-faint",
                      )}
                    >
                      {entry.title}
                    </span>
                    {entry.shortcut ? (
                      <span className="shrink-0 rounded bg-surface-2 px-1 text-[10px] text-muted-on-tint">
                        #{entry.shortcut}
                      </span>
                    ) : null}
                    {!entry.isActive ? (
                      <span className="shrink-0 text-[10px] uppercase text-faint">
                        Inactive
                      </span>
                    ) : null}
                  </div>
                  {/* A subject template has no body: its content is one line
                      of plain text, and running it through the HTML preview
                      would print an empty row under every entry. */}
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted">
                    {entry.kind === "subject"
                      ? (entry.subjectText ?? "")
                      : toPreview(entry.bodyHtml)}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEdit(entry)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      // Snippets are shared, so deleting one takes it away from
                      // everybody -- worth one confirmation.
                      if (
                        window.confirm(
                          `Delete "${entry.title}"? This removes it for everyone.`,
                        )
                      ) {
                        onDelete(entry.id);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </section>
  );
}

/**
 * Write one entry, of whichever kind.
 *
 * `kind` is a prop rather than a control, and is fixed for the life of the
 * form: on a new entry it comes from which button was pressed, and on an
 * existing one it is what the row already is. The server refuses to change a
 * saved entry's kind for a concrete reason -- the two are stored in different
 * columns, so flipping one would point it at an empty column -- and a
 * dropdown here would offer something the save would then reject.
 */
function Editor({
  response,
  kind,
  existing,
  onDone,
  onCancel,
}: {
  response: CannedResponse | null;
  kind: CannedKind;
  existing: CannedResponse[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const isSubject = kind === "subject";

  const [title, setTitle] = useState(response?.title ?? "");
  const [shortcut, setShortcut] = useState(response?.shortcut ?? "");
  const [category, setCategory] = useState(response?.category ?? "");
  /*
   * The body is HTML now, and the editor is uncontrolled -- it seeds itself
   * from `initialBody` on mount and reports every change into `body`. Captured
   * in a ref rather than recomputed, because a value that changed identity
   * would re-seed the surface under somebody mid-sentence.
   */
  const editor = useRef<RichTextHandle>(null);
  const initialBody = useRef(editableHtml(response?.bodyHtml ?? "")).current;
  const [body, setBody] = useState(initialBody);
  const [subject, setSubject] = useState(response?.subjectText ?? "");
  const [error, setError] = useState<string | null>(null);

  /* The one field that matters, whichever kind this is. Named so the guards
     below read the same for both rather than branching three times. The body
     is read as text: the guards ask whether anything was written and which
     placeholders are in it, and neither question is about the markup. */
  const content = isSubject ? subject : plainTextOf(body);

  const save = useMutation({
    mutationFn: () => {
      const common = {
        title: title.trim(),
        shortcut: shortcut.trim() ? shortcut.trim().toLowerCase() : null,
        category: category.trim() || null,
      };

      /* A patch never carries `kind` -- it is not editable -- while a create
         must, because it is what decides which column the content lands in. */
      if (response) {
        return api.updateCannedResponse(response.id, {
          ...common,
          ...(isSubject
            ? { subjectText: subject.trim() }
            : { bodyHtml: storedHtml(body) }),
        });
      }

      return api.createCannedResponse({
        ...common,
        ...(isSubject
          ? { kind: "subject" as const, subjectText: subject.trim() }
          : { bodyHtml: storedHtml(body) }),
      });
    },
    onSuccess: onDone,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  /*
   * Checked here as well as by the unique index, so the agent gets a sentence
   * rather than a 500 from a constraint violation.
   *
   * Scoped to the same kind, matching the index: `#warranty` naming both a
   * subject template and a reply snippet is allowed, because the two are typed
   * into different boxes and neither becomes ambiguous where it is used.
   */
  const clash = existing.find(
    (entry) =>
      entry.id !== response?.id &&
      entry.kind === kind &&
      entry.shortcut &&
      entry.shortcut === shortcut.trim().toLowerCase(),
  );

  const invalidShortcut =
    shortcut.trim().length > 0 && !/^[a-z0-9-]+$/i.test(shortcut.trim());

  const canSave =
    title.trim().length > 0 &&
    content.trim().length > 0 &&
    !clash &&
    !invalidShortcut &&
    !save.isPending;

  /*
   * The form opens above the list, so pressing Edit on the fifth snippet down
   * opened it somewhere the agent was not looking -- the page appeared to do
   * nothing, and was reported as "unable to edit canned responses". Brought
   * to wherever they are instead. The title's own `autoFocus` puts the caret
   * there so typing can start.
   */
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    frame.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, []);

  return (
    <div ref={frame} className="mb-6 scroll-mt-4 rounded-lg border border-line bg-surface p-4">
      {/* Which of the two is being written, said once at the top. The form is
          otherwise nearly identical between kinds, and an agent who pressed
          the wrong button should find that out before typing a paragraph. */}
      <h2 className="mb-3 text-sm font-medium text-content">
        {response ? "Edit" : "New"}{" "}
        {isSubject ? "subject template" : "reply snippet"}
      </h2>

      <div className="mb-3 grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <Field label="Title">
          <input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Returns process"
            className={inputClass}
          />
        </Field>

        <Field label="Shortcut" hint="Typed as #shortcut">
          <input
            value={shortcut}
            onChange={(event) => setShortcut(event.target.value)}
            placeholder="returns"
            className={inputClass}
          />
        </Field>

        <Field label="Category">
          <input
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder="Returns"
            className={inputClass}
          />
        </Field>
      </div>

      {isSubject ? (
        /*
         * One line, and an `<input>` rather than a textarea, because a subject
         * that spans lines is not a subject -- the server refuses newlines
         * outright, since everything after one becomes a header of its own on
         * the way out through Gmail. A control that cannot produce the thing
         * the server refuses is better than an error message about it.
         */
        <Field
          label="Subject"
          hint="One line. [[double brackets]] mark something that must be filled in."
        >
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={255}
            placeholder="Your return, ref [[INSERT RMA HERE]]"
            className={inputClass}
          />
        </Field>
      ) : (
        <Field
          label="Body"
          hint="Bold, lists and links are kept when the snippet is inserted. [[double brackets]] mark something that must be filled in."
        >
          {/*
            The same editor and the same toolbar as the reply box, which is the
            point: a snippet is written once and sent hundreds of times, so it
            is the worst text on the desk to leave as unformattable plain
            paragraphs -- and whatever is written here is dropped straight into
            that editor, so anything it cannot express is formatting the agent
            would have to add by hand on every send.

            Placeholders stay as the brackets somebody typed while they are
            being written, rather than appearing as the red span they become.
            Editing a placeholder you cannot select the ends of is worse than
            seeing it plainly, and the line under the editor already shows how
            they will read.
          */}
          <div className="flex items-center gap-0.5 rounded-t-md border border-b-0 border-line bg-surface-2 px-1 py-1">
            <FormatToolbar editor={editor} onChange={setBody} />
          </div>
          <div className="rounded-b-md border border-line">
            <RichText
              ref={editor}
              initialHtml={initialBody}
              onInput={setBody}
              placeholder="Write the snippet… Your return has been processed under the reference [[INSERT RMA HERE]]."
              surfaceHeight="min-h-32"
            />
          </div>
        </Field>
      )}

      {/*
        Shown as it will appear in the composer. A placeholder only works if the
        person writing the snippet can see that it stands out, and the textarea
        shows raw brackets.
      */}
      {/\[\[[^\][]+\]\]/.test(content) ? (
        <p className="mt-1.5 text-xs text-muted">
          Placeholders:{" "}
          {[...content.matchAll(/\[\[([^\][]+)\]\]/g)].map((match, index) => (
            <span key={index}>
              {index > 0 ? ", " : ""}
              <span className="font-bold text-red-600">{match[1]!.trim()}</span>
            </span>
          ))}
          .{" "}
          {isSubject
            ? "Fill it in after inserting — a subject is not checked on send the way a body is."
            : "A reply still containing one cannot be sent by accident."}
        </p>
      ) : null}

      {clash ? (
        <p className="mt-2 text-xs text-red-600">
          #{clash.shortcut} is already used by “{clash.title}”.
        </p>
      ) : null}
      {invalidShortcut ? (
        <p className="mt-2 text-xs text-red-600">
          Shortcuts can only contain letters, numbers and hyphens.
        </p>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-3 flex items-center gap-2">
        <Button onClick={() => save.mutate()} disabled={!canSave}>
          {save.isPending ? "Saving…" : response ? "Save changes" : "Create"}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const inputClass = cn(
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-1.5 text-xs text-muted">
        {label}
        {hint ? <span className="text-faint">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function groupByCategory(
  responses: CannedResponse[],
): [string, CannedResponse[]][] {
  const groups = new Map<string, CannedResponse[]>();
  for (const entry of responses) {
    const key = entry.category?.trim() || "Uncategorised";
    const list = groups.get(key);
    if (list) list.push(entry);
    else groups.set(key, [entry]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Snippets are authored as plain text with blank lines for paragraphs.
 *
 * A second rich-text surface here would have to round-trip HTML through an
 * editor on every load, and the bodies are short. Formatting that matters
 * (lists, links) can be added in the reply itself after inserting.
 */
/**
 * The editor's HTML as it is stored: `[[brackets]]` become placeholder spans.
 *
 * Walked as text nodes rather than run as a regex over the HTML string, which
 * is what this did when the body was plain text and escaped. The body now
 * carries real markup, and a link whose URL happened to contain brackets would
 * otherwise have a span spliced into the middle of its href.
 */
function storedHtml(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;

  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  while (walker.nextNode()) texts.push(walker.currentNode as Text);

  for (const node of texts) {
    /* An odd index is what was between the brackets -- `split` with a capture
       group alternates text, capture, text. */
    const parts = node.data.split(/\[\[([^\][]+)\]\]/g);
    if (parts.length === 1) continue;

    const replacement = document.createDocumentFragment();
    parts.forEach((part, index) => {
      if (index % 2 === 1) {
        replacement.append(placeholder(part.trim()));
      } else if (part !== "") {
        replacement.append(document.createTextNode(part));
      }
    });

    node.replaceWith(replacement);
  }

  return host.innerHTML;
}

/**
 * `[[like this]]` becomes a placeholder the agent cannot miss.
 *
 * Bold and red, and carrying `data-placeholder` so the composer can refuse to
 * send a reply that still has one in it. The colour is inline rather than a
 * class because this HTML is emailed: by the time a customer sees it, the app's
 * stylesheet is long gone, and a placeholder that reached them as plain text
 * would be the exact failure this is meant to prevent.
 *
 * Built as a node with `textContent` rather than as a string, so whatever was
 * between the brackets needs no escaping of its own.
 */
function placeholder(inner: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.setAttribute("data-placeholder", "1");
  span.setAttribute("style", "color:#dc2626;font-weight:700");
  span.textContent = inner;
  return span;
}

/**
 * The stored HTML as the editor should show it: placeholders back to brackets.
 *
 * The inverse of `storedHtml`, and the reason a snippet can be edited twice
 * without its placeholders quietly becoming ordinary red text.
 */
function editableHtml(stored: string): string {
  if (stored === "") return "";

  const host = document.createElement("div");
  host.innerHTML = stored;

  for (const node of host.querySelectorAll("[data-placeholder]")) {
    node.replaceWith(`[[${node.textContent ?? ""}]]`);
  }

  return host.innerHTML;
}

/** What a body says, with the markup taken off -- for the guards, not for display. */
function plainTextOf(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;
  return (host.textContent ?? "").trim();
}
